import uniqBy from 'lodash/uniqBy'
import { UseGetProps, UseGetReturn } from 'restful-react'
import { IAutocompleteBaseOption } from '../types'
import { getAutocompleteOptions } from '../utils'
import { IGetAutocompleteOptionsConfig } from '../utils/getAutocompleteOptions'

type TBaseProps<D, Q> = UseGetProps<D, unknown, Q, any>

type TUseGetHook<D, Q> = (props: Omit<TBaseProps<D, Q>, 'path'>) => UseGetReturn<D, unknown, Q, any>

export interface IAutocompleteOptionsProps<D = any, M = any> {
  /**
   * Programmatically adds a value to the options list
   * and filters it out when API results produce a duplicate
   * @default null
   * @see https://v4.mui.com/components/autocomplete/#google-maps-place
   */
  value?: M | null
  /**
   * Uses data from graphql and returns an array of options
   * @note Use `getAutocompleteOptions` to destructure the data and create fallbacks
   */
  getOptionsConfig: (
    data: D | undefined
  ) => IGetAutocompleteOptionsConfig<M> | IGetAutocompleteOptionsConfig<M>[]
}

export const useAutocompleteRestfulQuery = <D, Q>(
  useQueryHook: TUseGetHook<D, Q>,
  hookProps: Omit<TBaseProps<D, Q>, 'path'>,
  autocompleteProps: IAutocompleteOptionsProps<D>
): UseGetReturn<D, any, any, any> & { options: IAutocompleteBaseOption[] } => {
  const { getOptionsConfig, value = null } = autocompleteProps || {}
  const { data, ...rest } = useQueryHook(hookProps)
  const optionsConfig = getOptionsConfig(data || undefined)

  let apiResults: IAutocompleteBaseOption[] = []
  let options: IAutocompleteBaseOption[] = []

  if (Array.isArray(optionsConfig)) {
    for (const config of optionsConfig) {
      apiResults = [...apiResults, ...getAutocompleteOptions(config)]
    }
  } else {
    apiResults = getAutocompleteOptions(optionsConfig)
  }

  /**
   * If there's a value, add it to the options
   */
  if (value) {
    options = [value]
  }

  /**
   * Combine API response and value prop
   */
  if (apiResults) {
    options = uniqBy([...apiResults, ...options], 'id')
  }

  return {
    data,
    options,
    ...rest,
  }
}
